import { Moment } from 'moment';
import { momentLocaleFor } from '@shared/utils/moment-locale.utils';
import { hdAbsoluteDegreesFormat } from '../chart/bodygraph/gate-info/variants/hd-zodiac-position.util';
import { buildEphemerisRows } from './ephemeris-rows.utils';
import { EphemerisNotation, buildEphemerisDailyRows, toEphemerisDailyCell } from './ephemeris-daily.utils';
import { EphemerisMode } from './ephemeris-view-prefs';
import {
  EPHEMERIS_BODY_KEYS,
  EphemerisBodyKey,
  EphemerisBodyState,
  EphemerisDailyTable,
  EphemerisMoment,
  EphemerisPosition,
  EphemerisTable,
} from './ephemeris.models';
import { ephemerisMomentKey } from './ephemeris-inserted-moments';

/** Vertaalde vaste teksten die de builder nodig heeft (de component levert ze aan). */
export interface EphemerisDisplayTexts {
  readonly begin: string;
  readonly stationRetrograde: string;
  readonly stationDirect: string;
}

export interface EphemerisDisplayInput {
  readonly mode: EphemerisMode;
  readonly notation: EphemerisNotation;
  readonly useUtc: boolean;
  readonly lang: string;
  readonly now: Moment;
  readonly hidden: ReadonlySet<EphemerisBodyKey>;
  /** Wissels (modus `changes`). */
  readonly table: EphemerisTable | undefined;
  /** Dagposities (modus `daily`). */
  readonly daily: EphemerisDailyTable | undefined;
  /** Geladen ingevoegde momenten; enkel die binnen de periode worden getoond. */
  readonly inserted: readonly EphemerisMoment[];
  /** Tooltip voor een positie (poort.lijn.kleur.toon.basis · graden · zodiac). */
  readonly tooltipFor: (longitude: number | undefined) => string;
  readonly texts: EphemerisDisplayTexts;
}

export interface EphemerisDisplayColumn {
  readonly key: EphemerisBodyKey;
  /** Unieke kolomsleutel (selectie): de lichaamssleutel, of `moon-later` voor de Maan +6u. */
  readonly colKey: string;
  readonly moonLater: boolean;
}

export interface EphemerisDisplayCell {
  /** `32.4`, `11.6.3.3.3`, `267,0806°` of `27°04'50"`. */
  readonly text: string;
  /** Astronomicon-tekenglyph (enkel zodiac). */
  readonly signGlyph: string | null;
  readonly retrograde: boolean;
  /** Gewijzigd op dit moment (enkel wissels). */
  readonly changed: boolean;
  readonly station: 'retrograde' | 'direct' | null;
  readonly tooltip: string | null;
  /** Numerieke graad voor export, enkel waar de cel graden toont. */
  readonly degrees: number | null;
}

export type EphemerisDisplayRowKind = 'initial' | 'state' | 'inserted';

/** Zon- en maanstand op het rijmoment (Classic I Ching: hexagram van de dag), onafhankelijk van verborgen kolommen. */
export interface EphemerisIChingStates {
  readonly sun: EphemerisBodyState;
  readonly moon: EphemerisBodyState;
}

export interface EphemerisDisplayRow {
  readonly id: string;
  readonly kind: EphemerisDisplayRowKind;
  readonly atUtc: Moment;
  /** Het rijmoment in de weergavetijdzone. */
  readonly at: Moment;
  /** Dag- (wissels) of maandkop (dagposities) vóór deze rij. */
  readonly groupLabel: string | undefined;
  /** Datum in de tijdkolom (dagposities: eerste rij van een dag). */
  readonly dateLabel: string | undefined;
  /** Voorvoegsel in de tijdkolom ("Begin"). */
  readonly prefix: string | undefined;
  readonly time: string;
  readonly seconds: string | undefined;
  readonly cells: readonly EphemerisDisplayCell[];
  /** Zon en maan op het rijmoment (Classic I Ching); ontbreekt bij rijen zonder beide standen. */
  readonly iching?: EphemerisIChingStates;
}

export interface EphemerisDisplay {
  readonly mode: EphemerisMode;
  readonly fromUtc: Moment;
  readonly toUtc: Moment;
  readonly columns: readonly EphemerisDisplayColumn[];
  readonly rows: readonly EphemerisDisplayRow[];
  /** Index van de rij die nu geldt (de nu-markering komt erna); `-1` als nu buiten de periode valt. */
  readonly currentIndex: number;
}

const BODY_INDEX: ReadonlyMap<EphemerisBodyKey, number> = new Map(EPHEMERIS_BODY_KEYS.map((key, index) => [key, index]));

/**
 * Eén weergavelijst voor de tabel, de afdruk en de export (wat je ziet is wat je print of
 * exporteert): de wissel- of dagrijen, aangevuld met de ingevoegde rijen binnen de periode,
 * op tijd gesorteerd, met dag- of maandkoppen en de rij die nu geldt.
 */
export function buildEphemerisDisplay(input: EphemerisDisplayInput): EphemerisDisplay | undefined {
  return input.mode === 'daily' ? buildDaily(input) : buildChanges(input);
}

function buildChanges(input: EphemerisDisplayInput): EphemerisDisplay | undefined {
  const table = input.table;
  if (!table) return undefined;
  const vm = buildEphemerisRows(table, input.hidden);
  const columns: EphemerisDisplayColumn[] = vm.columns.map(key => ({ key, colKey: key, moonLater: false }));
  const inZone = zoneOf(input.useUtc);
  const ichingByTime = ichingStatesByTime(table);

  const stateRows: Draft[] = vm.rows.map(row => ({
    id: `s-${row.id}`,
    kind: 'state',
    atUtc: row.atUtc,
    iching: ichingByTime.get(row.atUtc.valueOf()),
    cells: row.cells.map(cell => ({
      text: cell.text,
      signGlyph: null,
      retrograde: cell.retrograde,
      changed: cell.changed,
      station: cell.station,
      tooltip: joinTooltip(
        cell.station === 'retrograde' ? input.texts.stationRetrograde : cell.station === 'direct' ? input.texts.stationDirect : '',
        input.tooltipFor(cell.longitude),
      ),
      degrees: null,
    })),
  }));
  const insertedRows: Draft[] = insertedWithin(input.inserted, table.fromUtc, table.toUtc).map(moment => ({
    id: `i-${ephemerisMomentKey(moment.atUtc)}`,
    kind: 'inserted',
    atUtc: moment.atUtc,
    iching: ichingOfPositions(moment.positions, EPHEMERIS_BODY_KEYS),
    cells: columns.map(column => {
      const position = moment.positions[BODY_INDEX.get(column.key) ?? 0];
      return {
        text: `${position.gate}.${position.line}.${position.color}.${position.tone}.${position.base}`,
        signGlyph: null,
        retrograde: position.retrograde,
        changed: false,
        station: null,
        tooltip: joinTooltip('', input.tooltipFor(position.longitude)),
        degrees: null,
      };
    }),
  }));

  const initial: Draft = {
    id: 'initial',
    kind: 'initial',
    atUtc: table.fromUtc,
    iching: ichingOfPositions(table.initial, table.bodies),
    cells: vm.initialCells.map(cell => ({
      text: cell.text,
      signGlyph: null,
      retrograde: cell.retrograde,
      changed: false,
      station: null,
      tooltip: joinTooltip('', input.tooltipFor(cell.longitude)),
      degrees: null,
    })),
  };

  const locale = momentLocaleFor(input.lang);
  let previousDay = '';
  const rows = [initial, ...mergeByTime(stateRows, insertedRows)].map((draft): EphemerisDisplayRow => {
    const at = inZone(draft.atUtc);
    const dayKey = at.format('YYYY-MM-DD');
    const groupLabel = dayKey !== previousDay ? at.clone().locale(locale).format('dddd D MMMM YYYY') : undefined;
    previousDay = dayKey;
    return {
      ...draft,
      at,
      groupLabel,
      dateLabel: undefined,
      prefix: draft.kind === 'initial' ? input.texts.begin : undefined,
      time: at.format('HH:mm'),
      seconds: draft.kind === 'initial' ? undefined : at.format(':ss'),
    };
  });

  return {
    mode: 'changes',
    fromUtc: table.fromUtc,
    toUtc: table.toUtc,
    columns,
    rows,
    currentIndex: currentIndexOf(rows, input.now, table.fromUtc, table.toUtc),
  };
}

function buildDaily(input: EphemerisDisplayInput): EphemerisDisplay | undefined {
  const daily = input.daily;
  if (!daily) return undefined;
  const vm = buildEphemerisDailyRows(daily, input.hidden, input.notation, input.lang);
  const columns: EphemerisDisplayColumn[] = vm.columns.map(column => ({
    key: column.key,
    colKey: column.moonLater ? 'moon-later' : column.key,
    moonLater: column.moonLater,
  }));
  const numberFormat = hdAbsoluteDegreesFormat(input.lang);
  const degreesFor = (longitude: number): number | null => (input.notation === 'degrees' ? longitude : null);

  const dayByTime = new Map(daily.days.map(day => [day.atUtc.valueOf(), day] as const));
  const dayRows: Draft[] = vm.rows.map(row => ({
    id: `d-${row.id}`,
    kind: 'state',
    atUtc: row.atUtc,
    iching: ichingOfPositions(dayByTime.get(row.atUtc.valueOf())?.positions, daily.bodies),
    cells: row.cells.map(cell => ({
      text: cell.text,
      signGlyph: cell.signGlyph,
      retrograde: cell.retrograde,
      changed: false,
      station: null,
      tooltip: joinTooltip('', input.tooltipFor(cell.longitude)),
      degrees: degreesFor(cell.longitude),
    })),
  }));
  const insertedRows: Draft[] = insertedWithin(input.inserted, daily.fromUtc, daily.toUtc).map(moment => ({
    id: `i-${ephemerisMomentKey(moment.atUtc)}`,
    kind: 'inserted',
    atUtc: moment.atUtc,
    iching: ichingOfPositions(moment.positions, EPHEMERIS_BODY_KEYS),
    cells: columns.map(column => {
      const position: EphemerisPosition = column.moonLater
        ? moment.moonLater
        : moment.positions[BODY_INDEX.get(column.key) ?? 0];
      const cell = toEphemerisDailyCell(position, input.notation, numberFormat);
      return {
        text: cell.text,
        signGlyph: cell.signGlyph,
        retrograde: cell.retrograde,
        changed: false,
        station: null,
        tooltip: joinTooltip('', input.tooltipFor(cell.longitude)),
        degrees: degreesFor(cell.longitude),
      };
    }),
  }));

  const inZone = zoneOf(input.useUtc);
  const locale = momentLocaleFor(input.lang);
  let previousDay = '';
  let previousMonth = '';
  const rows = mergeByTime(dayRows, insertedRows).map((draft): EphemerisDisplayRow => {
    const at = inZone(draft.atUtc);
    const dayKey = at.format('YYYY-MM-DD');
    const monthKey = at.format('YYYY-MM');
    const row: EphemerisDisplayRow = {
      ...draft,
      at,
      groupLabel: monthKey !== previousMonth ? at.clone().locale(locale).format('MMMM YYYY') : undefined,
      dateLabel: dayKey !== previousDay ? at.clone().locale(locale).format('dd D MMM') : undefined,
      prefix: undefined,
      time: at.format('HH:mm'),
      seconds: draft.kind === 'inserted' ? at.format(':ss') : undefined,
    };
    previousDay = dayKey;
    previousMonth = monthKey;
    return row;
  });

  return {
    mode: 'daily',
    fromUtc: daily.fromUtc,
    toUtc: daily.toUtc,
    columns,
    rows,
    currentIndex: currentIndexOf(rows, input.now, daily.fromUtc, daily.toUtc),
  };
}

/** Rij vóór tijdzone, koppen en tijdteksten. */
interface Draft {
  readonly id: string;
  readonly kind: EphemerisDisplayRowKind;
  readonly atUtc: Moment;
  readonly cells: readonly EphemerisDisplayCell[];
  readonly iching?: EphemerisIChingStates;
}

/** Zon en maan uit een positielijst in de volgorde van `bodies`; undefined als een van beide ontbreekt. */
function ichingOfPositions(
  positions: readonly EphemerisBodyState[] | undefined,
  bodies: readonly EphemerisBodyKey[],
): EphemerisIChingStates | undefined {
  if (!positions) return undefined;
  const sun = positions[bodies.indexOf('sun')];
  const moon = positions[bodies.indexOf('moon')];
  if (!sun || !moon) return undefined;
  return { sun: { gate: sun.gate, line: sun.line, retrograde: sun.retrograde }, moon: { gate: moon.gate, line: moon.line, retrograde: moon.retrograde } };
}

/** Per wisselrij (op tijd) de zon- en maanstand ná de wissel, cumulatief vanaf de beginstand. */
function ichingStatesByTime(table: EphemerisTable): ReadonlyMap<number, EphemerisIChingStates | undefined> {
  const states: EphemerisBodyState[] = [...table.initial];
  const byTime = new Map<number, EphemerisIChingStates | undefined>();
  for (const row of table.rows) {
    for (const change of row.changes) {
      states[change.body] = { gate: change.gate, line: change.line, retrograde: change.retrograde };
    }
    byTime.set(row.atUtc.valueOf(), ichingOfPositions(states, table.bodies));
  }
  return byTime;
}

function zoneOf(useUtc: boolean): (utc: Moment) => Moment {
  return utc => (useUtc ? utc.clone().utc() : utc.clone().local());
}

function insertedWithin(inserted: readonly EphemerisMoment[], fromUtc: Moment, toUtc: Moment): readonly EphemerisMoment[] {
  return inserted.filter(moment => !moment.atUtc.isBefore(fromUtc) && moment.atUtc.isBefore(toUtc));
}

/** Samenvoegen op tijd; bij gelijke tijd komt de basisrij eerst. */
function mergeByTime(base: readonly Draft[], extra: readonly Draft[]): Draft[] {
  const sortedExtra = [...extra].sort((a, b) => a.atUtc.valueOf() - b.atUtc.valueOf());
  const result: Draft[] = [];
  let e = 0;
  for (const row of base) {
    while (e < sortedExtra.length && sortedExtra[e].atUtc.valueOf() < row.atUtc.valueOf()) {
      result.push(sortedExtra[e++]);
    }
    result.push(row);
  }
  return result.concat(sortedExtra.slice(e));
}

function currentIndexOf(rows: readonly EphemerisDisplayRow[], now: Moment, fromUtc: Moment, toUtc: Moment): number {
  if (now.isBefore(fromUtc) || !now.isBefore(toUtc)) return -1;
  let index = -1;
  rows.forEach((row, i) => {
    if (!row.atUtc.isAfter(now)) index = i;
  });
  return index;
}

function joinTooltip(prefix: string, position: string): string | null {
  return [prefix, position].filter(part => part.length > 0).join(' · ') || null;
}
